
































































































































































import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { merge, omit } from 'lodash';

import { ErrorManager, IOrder } from '@/models';
import { IOrderFilterParam } from '@/services/order';

import mixins from '@/utils/mixins';
import functions from '@/mixins/functions';

import { createFile } from '@/utils/helpers';

import ALPage from '@/components/core/Page/ALPage.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';

const baseMixins = mixins(functions);

export default baseMixins.extend({
  name: 'OrderListPage',

  components: { ALPage, ConfirmationDialog, ErrorAlert },

  data() {
    return {
      sortBy: 'updated_at',
      sortDesc: true,
      deleteDialog: false,
      selectedOrder: null as IOrder | null,

      loading: true,
      headers: [
        {
          text: 'Series',
          align: 'start',
          value: 'series',
          sortable: false
        },
        { text: 'Customer Num.', value: 'customer_num', sortable: false },
        { text: 'Currency', value: 'currency', sortable: false },
        { text: 'Proposal #', value: 'refb', sortable: false },
        {
          text: 'Customer Purchase Order Number',
          value: 'eono',
          sortable: false
        },
        { text: 'Last Modified', value: 'updated_at', sortable: false },
        { text: '', value: 'actions', sortable: false }
      ],

      params: {
        search: this.$route.query.search as string,
        page: this.$route.query.page ? Number(this.$route.query.page) : 1
      },

      errorMessage: '',
      detailErrorMessage: '',
      deleteErrorMessage: ''
    };
  },

  computed: {
    ...mapGetters({
      orders: 'order/getOrders',
      numOrders: 'order/getOrderCount'
    })
  },

  watch: {
    'params.search'(search: string) {
      this.debounceFetchOrders({
        ...this.params,
        search: search,
        page: 1
      }).then(() => {
        this.params.page = 1;
      });
    }
  },

  async mounted() {
    // update `this.params` from the route query object.
    // omitting `search` and `page` as they are already set in the data vue option
    merge(this.params, omit(this.$route.query, ['page', 'search']));
    this.loading = true;

    this._fetchOrders(this.params);
  },

  methods: {
    moment,

    ...mapActions({
      fetchOrders: 'order/fetchOrders',
      deleteOrder: 'order/deleteOrder',
      exportOrderPriceDistribution: 'order/exportOrderPriceDistribution'
    }),

    clearAllErrors(): void {
      this.errorMessage = '';
      this.detailErrorMessage = '';
      this.deleteErrorMessage = '';
    },

    async debounceFetchOrders(params: IOrderFilterParam) {
      this.clearAllErrors();
      this.loading = true;

      return this.debounce(this._fetchOrders, 200)(params);
    },

    onUpdatePage(newPage: number) {
      if (newPage != this.params.page) {
        this.debounceFetchOrders({
          ...this.params,
          page: newPage
        }).then(() => {
          this.params.page = newPage;
        });
      }
    },

    _fetchOrders(params: IOrderFilterParam) {
      /* this will update the URL query based on param values. */
      this.$router
        .replace({
          name: 'order-list',
          query: params as Record<string, any>
        })
        .catch((e: Error) => e); //catch navigation duplication error

      return this.fetchOrders(params)
        .catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
          if (error.response) {
            if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * @param order OrderType
     * any if place of OrderType
     */
    exportPriceDistribution(order: IOrder) {
      this.exportOrderPriceDistribution(order.series)
        .then((data: Blob) => {
          createFile(
            data,
            `${order.series}-price-proposal-${moment(new Date()).format(
              'YYYY-MM-DD-HH-mm-ss'
            )}.xlsx`
          );
        })
        .catch(() => {
          throw new Error(
            'Error downloading price proposal at this time. See administrator for assistance'
          );
        });
    },

    openOrderDeleteDialog(order: IOrder) {
      this.selectedOrder = order;
      this.deleteDialog = true;
    },

    removeOrder(order: IOrder) {
      this.clearAllErrors();
      this.loading = true;
      this.deleteOrder(order.series)
        .then(() => {
          this.deleteDialog = false;
        })
        .catch(error => {
          this.deleteErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    selectOrder(order: IOrder) {
      this.$router.push({
        name: 'order-detail',
        params: { series: order.series }
      });
    },

    cancelOrderDeleteDialog() {
      this.selectedOrder = null;
      this.deleteDialog = false;
    },

    reopenUpload() {
      const upload = (this as any).$refs.uploadfile;
      upload.dialog = true;
    }
  }
});
